import React from "react";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Card,
    CardBody,
    Flex,
    Heading,
    Spacer,
    Text,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { Link } from "react-router-dom";

/** One step of the {@link PageHeader} breadcrumb trail. */
export interface PageHeaderCrumb {
    /** Text shown for this step. */
    label: string;
    /** Route to link to; omit on the last step to mark it as the current page. */
    to?: string;
}

/** Visual identity of the banner. Add a new key here + a theme color to extend. */
export type PageHeaderVariant = "default" | "admin" | "tools" | "ytg";

/** Banner background per variant — colours live in the theme (see `Theme.ts`). */
const BANNER_BG: Record<PageHeaderVariant, string> = {
    default: "principal.grey",
    admin: "brand.admin.main",
    // Tools section identity: neutral grey (same token as default, named for intent).
    tools: "principal.grey",
    // YourText.Guru section identity: the YTG brand purple.
    ytg: "brand.ytg.main",
};

interface PageHeaderProps {
    /** Breadcrumb trail, oldest first. The last item is rendered as current. */
    breadcrumbs: PageHeaderCrumb[];
    /** Page title shown in the banner. */
    title: string;
    /**
     * Optional right-aligned slot (count badges, action buttons). Rendered in an
     * `HStack`; style children with the `whiteAlpha` scheme to match the banner.
     */
    actions?: React.ReactNode;
    /**
     * Visual identity: `"default"` (neutral grey) or `"admin"` (ruby). The colour
     * is a theme token, so pages opt in with one prop and never repeat styles.
     */
    variant?: PageHeaderVariant;
}

/**
 * Shared page banner: a coloured card carrying a breadcrumb, the page title and
 * an optional right-aligned actions slot. Factored out of the management-page
 * header so every page shares one look; `variant` picks the section identity
 * (grey by default, ruby for admin). The title/actions row wraps on narrow
 * screens so it holds on all displays.
 */
export const PageHeader = ({ breadcrumbs, title, actions, variant = "default" }: PageHeaderProps): JSX.Element => (
    <Card bg={BANNER_BG[variant]} color="white" shadow="lg" rounded="lg">
        <CardBody>
            <Breadcrumb
                spacing="8px"
                separator={<ChevronRightIcon color="whiteAlpha.600" />}
                mb={4}
                fontSize="sm"
                color="whiteAlpha.800"
            >
                {breadcrumbs.map((crumb, index) => {
                    // Only the last crumb is the current page (white/semibold). A
                    // middle crumb without `to` (e.g. a menu category) stays plain.
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <BreadcrumbItem key={crumb.label} isCurrentPage={isLast}>
                            {crumb.to ? (
                                <BreadcrumbLink as={Link} to={crumb.to} _hover={{ color: "white" }}>
                                    {crumb.label}
                                </BreadcrumbLink>
                            ) : (
                                <Text
                                    color={isLast ? "white" : "whiteAlpha.800"}
                                    fontWeight={isLast ? "semibold" : "normal"}
                                >
                                    {crumb.label}
                                </Text>
                            )}
                        </BreadcrumbItem>
                    );
                })}
            </Breadcrumb>
            <Flex align="center" gap={4} flexWrap="wrap">
                <Heading fontWeight="extrabold" letterSpacing="-0.02em" size={{ base: "md", md: "lg" }}>
                    {title}
                </Heading>
                {actions && (
                    <>
                        <Spacer />
                        {/* Wrap-safe: actions reflow instead of overflowing on narrow screens. */}
                        <Flex gap={3} align="center" flexWrap="wrap">
                            {actions}
                        </Flex>
                    </>
                )}
            </Flex>
        </CardBody>
    </Card>
);

export default PageHeader;
